import useSnackbarErrors from "@/hooks/useSnackbarErrors";
import { copyToClipboardWrapper } from "@/utils/clipBoardUtils";
import { useState, useMemo, ComponentProps } from "react";
import RichTooltip from "./RichTooltip";

interface ChildProps {
  copy: (content: string) => void;
}

interface Props {
  TooltipProps?: Partial<ComponentProps<typeof RichTooltip>["tooltipProps"]>;
  children: (props: ChildProps) => React.ReactElement<any>;
  disableHover?: boolean;
  actionText?: string;
}

/**
 * Render prop component that wraps element in a Tooltip that shows "Copied to clipboard!" when the
 * copy function is invoked
 */
export default function CopyToClipboard({
  TooltipProps = {},
  children,
  disableHover,
  actionText = "Copy",
}: Props) {
  const [showTooltip, setShowTooltip] = useState(false);
  const { setSnackbarErrorMessage } = useSnackbarErrors();

  const onCopy = async (content: string) => {
    if (await copyToClipboardWrapper(content)) setShowTooltip(true);
    else setSnackbarErrorMessage("Copying might be disabled on your browser");
  };

  const handleOnTooltipClose = () => {
    setShowTooltip(false);
  };

  const hoverDisabledProps = useMemo(
    () =>
      disableHover
        ? {
            open: showTooltip,
            disableHoverListener: disableHover,
            title: "Copied",
          }
        : {},
    [disableHover, showTooltip]
  );

  const title = showTooltip ? "Copied" : actionText;

  // Children are wrapped in a <> to allow Tooltip to forwardRef for MUI reaons
  return (
    <RichTooltip
      title={title}
      compact
      tooltipProps={{
        disableHoverListener: disableHover,
        arrow: true,
        leaveDelay: 100,
        onClose: handleOnTooltipClose,
        ...(TooltipProps || {}),
        ...hoverDisabledProps,
      }}
    >
      {children({ copy: onCopy }) as React.ReactElement<any>}
    </RichTooltip>
  );
}
